import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { fetchPartsIcnLifetime } from '@hub/bearing-hub/BearingHubPage/parts/fetchPartsIcnLifetime';
import { fetchPartsLifetime } from '@hub/bearing-hub/BearingHubPage/parts/fetchPartsLifetime';
import type {
  IbhIcnPartsRequestDto,
  IbhPartsRequestDto,
  IbhShape,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { IbhLifeTimeConfig } from '@hub/bearing-hub/common/types';
import { QueryStatusName } from '@hub/bearing-hub/common/ui/IbhQueryStatusHandler/IbhQueryStatusHandler';
import type { IbhPartsLifetimeResponseDto } from '@igus/iglidur-bearing-hub-api';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import {
  type IbhDimensions,
  isBearingParamsComplete,
  ShapeDimensionsDefinitionForm,
} from '@hub/bearing-hub/common/dimensions/dimensions';
import type { UnitSystem } from '@igus/kopla-domain';

type UseBearingPartsLifetimeApiProps = {
  enabled: boolean;
  shape: IbhShape;
  materials: string[];
  unitSystem: UnitSystem;
  productionMethods: IbhProductionMethods[];
  quantity: number;
  dimensions?: IbhDimensions;
  lifetimeConfig?: IbhLifeTimeConfig;
  session?: Session;
  initialData?: IbhPartsLifetimeResponseDto;
};

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export const useBearingPartsLifetimeApi = ({
  enabled,
  shape,
  materials,
  unitSystem,
  productionMethods,
  quantity,
  dimensions,
  lifetimeConfig,
  session,
  initialData,
}: UseBearingPartsLifetimeApiProps) => {
  const isOnlyIcnRequest =
    productionMethods?.length === 1 && productionMethods[0] === IbhProductionMethods.SEMIFINISHED;
  const configComplete = isBearingParamsComplete({ ...dimensions, shape, unitSystem } as IbhPartsRequestDto);
  const shouldFetchIcnLifetime =
    (productionMethods === undefined || productionMethods.includes(IbhProductionMethods.SEMIFINISHED)) &&
    configComplete;
  const ibhProductionMethods = productionMethods?.filter(method => method !== IbhProductionMethods.SEMIFINISHED);
  const icnMaterials = [
    ...new Set(materials.map(material => (material.endsWith('-CNC') ? material : `${material}-CNC`))),
  ];
  const isRequestEnabled = () => {
    if (!enabled) {
      return false;
    }
    if (!dimensions) {
      return true;
    }
    const isCompleteGeometry = ShapeDimensionsDefinitionForm[shape].length === Object.values(dimensions).length;
    return isCompleteGeometry;
  };
  const queryKey = [
    'bearing-parts-lifetime',
    dimensions,
    productionMethods,
    materials,
    unitSystem,
    shape,
    lifetimeConfig,
  ].filter(Boolean);
  const requestParams = {
    shape,
    unitSystem,
    quantity,
    lifetimeConfig,
    dimensions,
  };
  const serializedQueryKey = JSON.stringify(queryKey);
  const initialDataRef = useRef(initialData);
  const initialDataQueryKeyRef = useRef(serializedQueryKey);

  if (initialDataRef.current !== initialData) {
    initialDataRef.current = initialData;
    initialDataQueryKeyRef.current = serializedQueryKey;
  }

  const fetchLifetime = async () => {
    const [partsLifetime, icnPartsLifetime] = await Promise.all([
      isOnlyIcnRequest
        ? undefined
        : fetchPartsLifetime({ sid: session?.sessionId }, {
            ...requestParams,
            materials,
            productionMethods: ibhProductionMethods,
          } satisfies IbhPartsRequestDto),
      shouldFetchIcnLifetime
        ? fetchPartsIcnLifetime({ sid: session?.sessionId }, {
            ...requestParams,
            productionMethods,
            materials: icnMaterials,
            dimensions: dimensions as IbhIcnPartsRequestDto['dimensions'],
          } satisfies IbhIcnPartsRequestDto)
        : undefined,
    ]);

    return {
      lifetimes: {
        ...partsLifetime?.lifetimes,
        ...icnPartsLifetime?.lifetimes,
      },
    };
  };

  const query = useQuery<IbhPartsLifetimeResponseDto>({
    queryKey,
    queryFn: fetchLifetime,
    enabled: isRequestEnabled,
    // SSR data is calculated without dimensions; geometry queries need their own result.
    initialData: () =>
      !dimensions && initialDataQueryKeyRef.current === serializedQueryKey ? initialDataRef.current : undefined,
    placeholderData: initialData,
    gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
    retry: (failureCount: number) => failureCount <= 1 && navigator.onLine,
  }) as UseQueryResult<IbhPartsLifetimeResponseDto>;

  const previousData = useRef<IbhPartsLifetimeResponseDto | undefined>(undefined);
  useEffect(() => {
    if (query.data) {
      previousData.current = query.data;
    }
  }, [query.data, queryKey]);

  const lifeTimeData = query.data ?? previousData.current;
  const lifetimesValues = lifeTimeData?.lifetimes ? Object.values(lifeTimeData.lifetimes) : [];
  const maxLifeTimeReference = lifetimesValues.length > 0 ? Math.max(...lifetimesValues) : 0;

  return {
    lifeTimeData,
    maxLifeTimeReference,
    isLifetimeLoading: query.isFetching,
    status: enabled && query.data ? QueryStatusName.success : QueryStatusName.idle,
    refetchLifetime: query.refetch,
  };
};
